/**
 * chat-api.ts — HTTP client for the NestJS backend.
 *
 * Sends message to the backend and processes the SSE stream,
 * dispatching tool_call events to the tool executor.
 */

import type {
    SendMessageRequest,
    SseEvent,
    ToolResultRequest,
    ToolResultBatchRequest,
    WorkbookContext,
} from "excel-agent-core";
import { getAuthToken } from "../auth/auth-store";
import { deviceHeaders, getDeviceId } from "../auth/device-id";
import { i18n } from "../chat/i18n.svelte";

export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? "https://localhost:4000";

export type MessageHandler = (event: SseEvent) => void;

function authHeaders(): Record<string, string> {
    const token = getAuthToken();
    return {
        ...deviceHeaders(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

function localizeBackendMessage(message: string, status: number): string {
    if (/license has expired/i.test(message)) return "License của bạn đã hết hạn";
    if (/license is not active yet/i.test(message)) return "License của bạn chưa có hiệu lực";
    if (status === 503 && /maintenance/i.test(message)) return "Hệ thống đang bảo trì";
    return message;
}

async function throwHttpError(response: Response, fallback: string): Promise<never> {
    const body = await response.json().catch(() => null) as { message?: unknown; detail?: unknown } | null;
    const raw = body?.message ?? body?.detail;
    const message = typeof raw === "string" ? raw : fallback;
    throw new Error(localizeBackendMessage(message, response.status));
}

/**
 * Send a user message to the backend and handle the SSE stream.
 * Returns the sessionId assigned/confirmed by the backend.
 */
export async function sendMessage(
    message: string,
    workbookContext: WorkbookContext,
    skillIds: SendMessageRequest["skillIds"],
    commandSnippets: SendMessageRequest["commandSnippets"],
    sessionId: string | null,
    onEvent: MessageHandler,
    signal?: AbortSignal,
): Promise<string> {
    const body: SendMessageRequest = {
        sessionId: sessionId ?? crypto.randomUUID(),
        message,
        workbookContext,
        skillIds,
        commandSnippets,
        language: i18n.locale,
    };

    const response = await fetch(`${BACKEND_URL}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) await throwHttpError(response, `Backend error: ${response.status} ${response.statusText}`);
    if (!response.body) throw new Error(`Backend error: ${response.status} ${response.statusText}`);

    return await readSseStream(response.body, onEvent, body.sessionId, signal);
}

/**
 * Submit the result of an Office.js tool call back to the backend
 * so the LLM can continue generating.
 *
 * Legacy single-tool endpoint — still supported by BE as a 1-element
 * batch wrapper. New code paths should prefer `submitToolResultBatch`
 * which collects all sibling tool_results and POSTs them atomically
 * (Patch 5 — fixes the appendMessage race that caused silent UI freezes).
 */
export async function submitToolResult(
    request: ToolResultRequest,
    onEvent: MessageHandler,
    signal?: AbortSignal,
): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/chat/tool-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(request),
        signal,
    });

    if (!response.ok) await throwHttpError(response, `Backend error: ${response.status} ${response.statusText}`);
    if (!response.body) throw new Error(`Backend error: ${response.status} ${response.statusText}`);

    await readSseStream(response.body, onEvent, request.sessionId, signal);
}

/**
 * Patch 5 — atomic batched tool-results endpoint.
 *
 * The UI batch coordinator collects all N tool_results from one parent
 * assistant message and POSTs them here in a single request. The BE
 * persists all N atomically (no race possible) and fires runGraph once.
 *
 * Idempotent on `parentMessageId` server-side: a duplicate POST with the
 * same parent (e.g. our error-recovery re-fire) is a no-op.
 */
export async function submitToolResultBatch(
    request: ToolResultBatchRequest,
    onEvent: MessageHandler,
    signal?: AbortSignal,
): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/chat/tool-results-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(request),
        signal,
    });

    if (!response.ok) await throwHttpError(response, `Backend error: ${response.status} ${response.statusText}`);
    if (!response.body) throw new Error(`Backend error: ${response.status} ${response.statusText}`);

    await readSseStream(response.body, onEvent, request.sessionId, signal);
}

/**
 * Upload an attachment to a session.
 */
export async function uploadAttachment(
    sessionId: string,
    file: File,
    taskId?: string,
): Promise<{ id: string, filename: string, size: number, mimeType: string, extractedData: string, hasStructuredData?: boolean }> {
    const formData = new FormData();
    formData.append("file", file);

    const url = new URL(`${BACKEND_URL}/chat/session/${sessionId}/attachments`);
    if (taskId) url.searchParams.append("taskId", taskId);

    const response = await fetch(url.toString(), {
        method: "POST",
        headers: { ...authHeaders() },
        body: formData,
    });

    if (!response.ok) await throwHttpError(response, "Upload failed");

    return await response.json();
}

/**
 * Fetch the full extracted content of an attachment.
 */
export async function getAttachmentContent(attachmentId: string): Promise<string> {
    const response = await fetch(`${BACKEND_URL}/chat/attachment/${attachmentId}`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch attachment content");
    const data = await response.json();
    return data.content;
}

export async function getAttachmentStructuredDataPage(
    attachmentId: string,
    tableIndex: number,
    offset: number,
    limit: number,
): Promise<{ rows: string[][]; totalRows: number; hasMore: boolean }> {
    const params = new URLSearchParams({
        tableIndex: String(tableIndex),
        offset: String(offset),
        limit: String(limit),
    });
    const response = await fetch(
        `${BACKEND_URL}/chat/attachment/${attachmentId}/structured-data?${params}`,
        { headers: { ...authHeaders() } },
    );
    if (!response.ok) await throwHttpError(response, "Failed to fetch structured data page");
    return response.json();
}

/**
 * List all active sessions from the backend.
 */
export async function getSessions(): Promise<any[]> {
    const response = await fetch(`${BACKEND_URL}/chat/sessions`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch sessions");
    return await response.json();
}

export async function getLlmConfig(): Promise<any> {
    const response = await fetch(`${BACKEND_URL}/chat/llm-config`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch LLM config");
    return await response.json();
}

/**
 * Fetch the canonical list of built-in skills from the backend.
 * Response shape: { skills: Omit<SkillMeta, 'prompt' | 'files'>[] }
 * The `prompt` body is intentionally not exposed — it's BE-only.
 */
export async function getSkills(): Promise<any[]> {
    const response = await fetch(`${BACKEND_URL}/skills`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch skills");
    const body = await response.json();
    return Array.isArray(body?.skills) ? body.skills : [];
}

/** Fetch global system configuration. Returns partial object on auth error. */
export async function getGlobalConfig(): Promise<Record<string, string>> {
    const response = await fetch(`${BACKEND_URL}/admin/config`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) return {};
    return await response.json();
}

/** Update global system configuration (Admin only). */
export async function updateGlobalConfig(config: Record<string, string>): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/admin/config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(config),
    });
    if (!response.ok) await throwHttpError(response, "Failed to update global config");
}

export async function getSessionMessages(sessionId: string): Promise<any[]> {
    const response = await fetch(`${BACKEND_URL}/chat/${sessionId}/messages`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch session messages");
    return await response.json();
}

/**
 * Delete an attachment.
 */
export async function deleteAttachment(id: string): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/chat/attachment/${id}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to delete attachment");
}

/**
 * Get internal download URL for an attachment (contains token).
 */
export function getAttachmentDownloadUrl(id: string): string {
    const token = getAuthToken();
    return `${BACKEND_URL}/chat/attachment/${id}/download?token=${token}&device_id=${encodeURIComponent(getDeviceId())}`;
}

/**
 * Get all attachments for a specific session.
 */
export async function getSessionAttachments(sessionId: string): Promise<any[]> {
    const response = await fetch(`${BACKEND_URL}/chat/${sessionId}/attachments`, {
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to fetch session attachments");
    return await response.json();
}

/**
 * Delete a session from the backend.
 */
export async function deleteSession(sessionId: string): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/chat/${sessionId}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
    });
    if (!response.ok) await throwHttpError(response, "Failed to delete session");
}

/**
 * Parse an SSE (Server-Sent Events) stream and emit parsed events.
 * Returns the sessionId if a session_id event was received.
 */
async function readSseStream(
    body: ReadableStream<Uint8Array>,
    onEvent: MessageHandler,
    defaultSessionId: string,
    signal?: AbortSignal,
): Promise<string> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let confirmedSessionId = defaultSessionId;

    try {
        while (true) {
            if (signal?.aborted) {
                const err = new Error("Aborted");
                err.name = "AbortError";
                throw err;
            }
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n\n");
            buffer = lines.pop() ?? "";

            for (const chunk of lines) {
                const line = chunk.trim();
                if (!line.startsWith("data: ")) continue;

                try {
                    const data = JSON.parse(line.slice(6)) as Record<string, unknown>;

                    if (data.type === "session_id" && typeof data.sessionId === "string") {
                        confirmedSessionId = data.sessionId;
                        continue;
                    }

                    onEvent(data as unknown as SseEvent);
                } catch {
                    console.warn("[chat-api] Failed to parse SSE line:", line);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }

    return confirmedSessionId;
}
